
import React, { useEffect } from 'react';

import KStatus from '../base/data/KStatus';
import {KImpactStage} from '../base/data/Impact';
import C from '../C';
import Icon from '../base/components/Icon';
import PropControl from '../base/components/PropControl';
import NGO from '../base/data/NGO';
import { encURI, is } from '../base/utils/miscutils';
import DataStore from '../base/plumbing/DataStore';
import { getDataItem } from '../base/plumbing/Crud';
import CardAccordion, { Card } from '../base/components/CardAccordion';
import SubCard from '../base/components/SubCard';
import printer from '../base/utils/printer';
import PropControlPlace from '../base/components/propcontrols/PropControlPlace';
// Import so it's registered & use to guard against tree-shaking
const retainImports = [PropControlPlace];


function ImpactEditor({ ipath, stage }) {
	let impact = DataStore.getValue(ipath) || {};
	let unit = impact.name;
	let pvNGO = impact.charity && getDataItem({ type: C.TYPES.NGO, id: impact.charity, status: KStatus.PUBLISHED, swallow:true }) || {};
	useEffect(() => {
		// copy charity data into impact debit
		let ngo = pvNGO.value;
		if ( ! ngo) return;
		if ( ! impact.category) {
			impact.category = ngo.category;
		}
		if ( ! impact.unsdg) {
			impact.unsdg = ngo.UNSDG;
		}
	}, [pvNGO.value]);

	return (<div>
		<PropControl label="Impact partner" path={ipath} prop="charity" sort="id-asc"
			type="DataItem" itemType={C.TYPES.NGO} status={KStatus.PUBLISHED}
			canCreate
		/>
		{impact.charity && <small><C.A href={'/#ngo/' + encURI(impact.charity)}>edit NGO</C.A></small>}

		<PropControl type="select" path={ipath} prop="category" label
			options={Object.keys(NGO.CATEGORY)}
			help="The categories are mostly aligned with those used by Charity Navigator."
		/>
		<PropControl type="select" path={ipath} prop="subcategory" label="Sub-category"
			options={NGO.CATEGORY[impact.category] || []}
			disabled={!impact.category}
		/>
		<PropControl type="select" path={ipath} prop="unsdg" label="UN Sustainable Development Goal (SDG)"
			options={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17]}
			labels={NGO.UNSDGs}
			help="Which UN SDG does this charity mainly work on?"
		/>

		<PropControl label="Output type (e.g. carbon offset, tree(s), coral)" type="text" 
			prop="name" path={ipath}
			help="Type of output for this offset. Use 'carbon offset' or 'tree(s)' exactly if relevant."
		/>

		<PropControl type="number" prop="n" path={ipath} 
			label={<>{impact.name || "Output"} - units planted/committed<Icon name="seedling" /></>}
		/>
		<PropControl prop="unit" path={ipath} disabled={!unit} label='Measurement unit (e.g. "kg")' 
			help="Only used for some outputs. Always prefer SI units, e.g. kg rather than tons." />

		<PropControl type="number" prop="peoplePerOutput" path={ipath}
			label="People per output"
			help="Estimates how many people were helped. E.g. a workshop might reach 10 people, whilst a solar lamp helps a family of ~5."
		/>
		{impact.peoplePerOutput && impact.n? printer.prettyInt(impact.n*impact.peoplePerOutput)+" people helped" : null}

		<PropControl type="Money" prop="amount" path={ipath} label />
		<PropControl type="Money" prop="amountTarget" path={ipath} label="Amount Target" help="The total that should be raised." />

		<SubCard title="Dynamic Impacts">
			<PropControl type="toggle" prop="dynamic" path={ipath} label help="Use dynamic Impacts for running campaigns where the impact pledged goes up with each impression" />
			<PropControl prop="input" path={ipath} label placeholder="impressions" help="Input for dynamic impacts - rate is applied to this" />
			<PropControl type="Money"
				disabled={is(impact.dynamic)? ! impact.dynamic : stage && stage !== KImpactStage.PLANNING && stage !== KImpactStage.CAMPAIGN_OPEN} 
				prop="amountRate" path={ipath} label="Amount Rate"
				help="The amount per impression (NOT CPM) for dynamic amount during a campaign"
			/>
			<PropControl type="number"
				prop="rate" path={ipath} label
				help='Multiplier for proportional impacts (eg "1 tree per 10,000 impressions" = 1/10000 = 0.0001)'
			/>
			{impact.rate && "1 per "+printer.prettyNumber(1/impact.rate)}
		</SubCard>

		<PropControl type="number" prop="amountGBP" path={ipath} label="Amount in GBP" />

		<PropControl label type="date" prop="date" path={ipath} help="The date for the impact, e.g. when a tree was planted" />

		<PropControl label type="place" prop="location" path={ipath} warnOnUnpublished={false /* avoid bug for place */} />

		<CardAccordion>
			<Card title="Impact Story">
				<PropControl type="number" prop="priority" path={ipath} label="Priority" className="font-weight-bold"/>
				<PropControl type="number" prop="amountGBP" path={ipath} label="Amount In GBP" readOnly/>
				<h2>Props for Impact Stories</h2>
				<p>Only required if this impact is within the top 2 impacts for a brand or campaign.</p>
				<h3>Impact Story #1</h3>
				<div className="mb-3 p-3 bg-light card">
					<h3>Impact Cause</h3>
					<PropControl type="textarea" label="Impact Cause" prop="impactCause" path={ipath} help="In format 'Supporting {Impact cause}' For example, 'Supporting Food Redistribution'"/>
					<PropControl type="textarea" label="Impact Stat" prop="impactStats" path={ipath} help="What good did the brand do to support the above cause? For example, 'Providing meals for children in need'"/>
				</div>
				<div className="mb-3 p-3 bg-light card">
					<h3>Bitesize fact</h3>
					<p>Replaced by image if empty</p>
					<PropControl type="textarea" label="Did-You-Know Fact" prop="fact" path={ipath}/>
					<PropControl type="textarea" label="Sources Name" prop="factSourceName" path={ipath}/>
					<PropControl type="textarea" label="Source URL" prop="factSourceUrl" path={ipath} />
				</div>
				<h3>Impact Story #2</h3>
				<div className="mb-3 p-3 bg-light card">
					<h3>Impact Cause</h3>
					<PropControl type="textarea" label="Impact Cause" prop="impactCause" path={ipath} help="In format 'Supporting {Impact cause}' For example, 'Supporting Food Redistribution'"/>
					<PropControl type="textarea" label="Impact Stat" prop="impactStats" path={ipath} help="What good did the brand do to support the above cause? For example, 'Providing meals for children in need'"/>
				</div>
				<div className="mb-3 p-3 bg-light card">
					<h3>Testimonial</h3>
					<p>Defaults to description of charity if testimonial itself is left empty</p>
					<PropControl type="textarea" label="Testimonial Title" prop="testimonialHeader" path={ipath} help="Header of testimonial card"/>
					<PropControl type="textarea" label="Testimonial" prop="testimonialQuote" path={ipath} />
					<PropControl type="textarea" label="Testimonial Source Role" prop="testimonialJob" path={ipath} help="Role of whoever said the testimonial" />				
					<PropControl type="textarea" label="Testimonial Source" prop="testimonialPerson" path={ipath} help="Name of whoever said the testimonial"/>
				</div>
			</Card>
		</CardAccordion>

		<PropControl type="textarea" prop="notes" path={ipath} label="Internal notes" />
	</div >);
}

export default ImpactEditor;